import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Users, TrendingUp, Target, MessageSquare, Search,
  ArrowUpRight, ArrowDownRight, Phone, Award, BarChart3,
  ChevronRight, HelpCircle, Shield, Zap, ArrowLeft,
  CheckCircle2, XCircle, Sparkles, Loader2, UserPlus, Mail, Clock, Trash2, RotateCw
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function MetricBar({ label, value, icon: Icon, color = 'primary' }) {
  const colorMap = {
    primary: { bar: 'bg-primary', text: 'text-primary' },
    emerald: { bar: 'bg-emerald-500', text: 'text-emerald-600' },
    amber: { bar: 'bg-amber-500', text: 'text-amber-400' },
    rose: { bar: 'bg-rose-500', text: 'text-rose-400' },
    violet: { bar: 'bg-violet-500', text: 'text-violet-400' },
  };
  const c = colorMap[color] || colorMap.primary;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {Icon && <Icon className="w-3 h-3" />}
          {label}
        </div>
        <span className={`text-xs font-semibold ${c.text}`}>{value}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
        <div className={`h-full rounded-full ${c.bar} transition-all duration-700`} style={{ width: `${Math.min(value, 100)}%` }} />
      </div>
    </div>
  );
}

// Mini sparkline chart for trend
function Sparkline({ data, color = '#3b82f6', height = 40 }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 100);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const w = 200;
  const points = data.map((v, i) => `${(i / (data.length - 1)) * w},${height - ((v - min) / range) * (height - 4)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${height}`} className="w-full" style={{ height }}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={(data.length - 1) / (data.length - 1) * w} cy={height - ((data[data.length - 1] - min) / range) * (height - 4)} r="3" fill={color} />
    </svg>
  );
}

// Comparison metric card
function ComparisonCard({ label, first, second }) {
  const diff = second - first;
  const improved = diff > 0;
  return (
    <div className="p-3 rounded-lg bg-secondary/30 border border-border/50">
      <p className="text-xs text-muted-foreground mb-2">{label}</p>
      <div className="flex items-end justify-between">
        <div>
          <span className="text-lg font-bold text-foreground">{second}%</span>
          <span className="text-xs text-muted-foreground ml-1">now</span>
        </div>
        <div className={`flex items-center gap-1 text-xs font-medium ${improved ? 'text-emerald-600' : diff < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
          {improved ? <ArrowUpRight className="w-3.5 h-3.5" /> : diff < 0 ? <ArrowDownRight className="w-3.5 h-3.5" /> : null}
          {diff > 0 ? '+' : ''}{diff.toFixed(1)}%
        </div>
      </div>
      <div className="flex gap-1 mt-2">
        <div className="h-1 rounded-full bg-muted-foreground/20 flex-1">
          <div className="h-full rounded-full bg-muted-foreground/40" style={{ width: `${first}%` }} />
        </div>
        <div className={`h-1 rounded-full flex-1 ${improved ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
          <div className={`h-full rounded-full ${improved ? 'bg-emerald-500' : 'bg-red-500'}`} style={{ width: `${second}%` }} />
        </div>
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-[10px] text-muted-foreground">Earlier</span>
        <span className="text-[10px] text-muted-foreground">Recent</span>
      </div>
    </div>
  );
}

// Rep Detail View
function RepDetail({ userId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const res = await fetch(`${API_URL}/api/performance/rep/${userId}/progress`, { credentials: 'include' });
        if (res.ok) setData(await res.json());
      } catch (e) {
        toast.error('Failed to load progress data');
      } finally {
        setLoading(false);
      }
    };
    fetchProgress();
  }, [userId]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 animate-spin text-primary" />
    </div>
  );

  if (!data || data.total_calls === 0) return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
      <Card className="bg-card border-border/50">
        <CardContent className="p-12 text-center">
          <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Not enough data yet. Need at least 1 analyzed call.</p>
        </CardContent>
      </Card>
    </div>
  );

  const { user, trend, comparison, ai_summary, coaching_compliance } = data;

  return (
    <div className="space-y-6" data-testid="rep-detail-view">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="back-to-team">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h2 className="font-heading text-xl font-bold">{user.name}</h2>
          <p className="text-sm text-muted-foreground">{data.total_calls} calls analyzed</p>
        </div>
      </div>

      {/* AI Progress Summary */}
      {ai_summary && (
        <Card className="bg-card border-primary/20" data-testid="ai-progress-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              AI Progress Analysis
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-base font-medium text-foreground">{ai_summary.headline}</p>
            <p className="text-sm text-muted-foreground">{ai_summary.trend_insight}</p>

            <div className="grid gap-3 md:grid-cols-2 mt-2">
              <div>
                <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-1.5">Strengths</p>
                {ai_summary.strengths?.map((s, i) => (
                  <div key={i} className="flex items-start gap-2 mb-1">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                    <span className="text-xs text-foreground/85">{s}</span>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-1.5">Needs Work</p>
                {ai_summary.weaknesses?.map((w, i) => (
                  <div key={i} className="flex items-start gap-2 mb-1">
                    <XCircle className="w-3.5 h-3.5 text-red-600 mt-0.5 shrink-0" />
                    <span className="text-xs text-foreground/85">{w}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 mt-2">
              <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-1">Next Focus</p>
              <p className="text-sm text-foreground">{ai_summary.next_focus}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trend Charts */}
      {trend.length >= 2 && (
        <Card className="bg-card border-border/50" data-testid="trend-charts">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              Skill Trends Over Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[
                { key: 'discovery', label: 'Discovery', color: '#3b82f6' },
                { key: 'objection_handling', label: 'Objection Handling', color: '#f59e0b' },
                { key: 'closing', label: 'Closing / Next Steps', color: '#ef4444' },
                { key: 'deal_score', label: 'Deal Score', color: '#10b981' },
                { key: 'talk_ratio', label: 'Talk Ratio', color: '#8b5cf6' },
              ].map(({ key, label, color }) => {
                const values = trend.map(t => t[key] || 0);
                const last = values[values.length - 1];
                const first = values[0];
                const diff = last - first;
                return (
                  <div key={key} className="p-3 rounded-lg bg-secondary/30 border border-border/50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-muted-foreground">{label}</span>
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-bold" style={{ color }}>{last}</span>
                        {diff !== 0 && (
                          <span className={`text-[10px] flex items-center ${diff > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {diff > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                            {Math.abs(diff).toFixed(0)}
                          </span>
                        )}
                      </div>
                    </div>
                    <Sparkline data={values} color={color} height={36} />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Period Comparison */}
      {comparison && (
        <Card className="bg-card border-border/50" data-testid="period-comparison">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-primary" />
              {comparison.first_period.label} vs {comparison.second_period.label}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
              <ComparisonCard label="Discovery" first={comparison.first_period.discovery} second={comparison.second_period.discovery} />
              <ComparisonCard label="Objections" first={comparison.first_period.objection_handling} second={comparison.second_period.objection_handling} />
              <ComparisonCard label="Closing" first={comparison.first_period.closing} second={comparison.second_period.closing} />
              <ComparisonCard label="Talk Ratio" first={comparison.first_period.talk_ratio} second={comparison.second_period.talk_ratio} />
              <ComparisonCard label="Avg Score" first={comparison.first_period.avg_score} second={comparison.second_period.avg_score} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Coaching Compliance */}
      {coaching_compliance?.length > 0 && (
        <Card className="bg-card border-border/50" data-testid="coaching-compliance">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Target className="w-4 h-4 text-primary" />
              Coaching Compliance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {coaching_compliance.map((cc, idx) => (
              <div key={`${cc.from_company}-${cc.to_company}-${idx}`} className="p-3 rounded-lg bg-secondary/20 border border-border/50">
                <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
                  <span>{cc.from_company}</span>
                  <ChevronRight className="w-3 h-3" />
                  <span>{cc.to_company}</span>
                </div>
                {cc.items.map((item, i) => (
                  <div key={`${item.area}-${i}`} className="flex items-start gap-2 mb-1.5">
                    {item.improved ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-red-600 mt-0.5 shrink-0" />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium">{item.area}</span>
                        <span className="text-[10px] text-muted-foreground">{item.prev_score} → {item.curr_score}</span>
                        <span className={`text-[10px] font-medium ${item.improved ? 'text-emerald-600' : 'text-red-600'}`}>
                          {item.improved ? 'Improved' : 'No change'}
                        </span>
                      </div>
                      <p className="text-[10px] text-muted-foreground italic mt-0.5">Advice: "{item.advice}"</p>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function TeamInvitesPanel({ isManager }) {
  const [invites, setInvites] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [invRes, teamRes] = await Promise.all([
        fetch(`${API_URL}/api/teams/invitations`, { credentials: 'include' }),
        fetch(`${API_URL}/api/teams/my-team`, { credentials: 'include' }),
      ]);
      if (invRes.ok) setInvites(await invRes.json());
      if (teamRes.ok) {
        const data = await teamRes.json();
        setMembers(data.members || []);
      }
    } catch (e) {
      console.error('Team refresh failed:', e);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isManager) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManager]);

  if (!isManager) return null;

  const pendingInvites = invites.filter(i => i.status === 'pending');
  const pastInvites = invites.filter(i => i.status !== 'pending').slice(0, 5);

  const handleInvite = async () => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/teams/invitations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: e }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || 'Failed to invite');
      toast.success(`Invitation sent to ${e}`);
      setEmail('');
      setShowDialog(false);
      refresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this invitation?')) return;
    const res = await fetch(`${API_URL}/api/teams/invitations/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (res.ok) { toast.success('Invitation revoked'); refresh(); }
    else toast.error('Failed to revoke');
  };

  const handleResend = async (id) => {
    const res = await fetch(`${API_URL}/api/teams/invitations/${id}/resend`, {
      method: 'POST',
      credentials: 'include',
    });
    if (res.ok) toast.success('Invitation resent');
    else toast.error('Failed to resend');
  };

  return (
    <Card className="bg-card border-border/60" data-testid="team-invites-panel">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div>
          <CardTitle className="font-heading text-lg">Team Members</CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            {members.length} member{members.length !== 1 ? 's' : ''}
            {pendingInvites.length > 0 && `  •  ${pendingInvites.length} pending`}
          </p>
        </div>
        <Button
          onClick={() => setShowDialog(true)}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="invite-member-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Invite Member
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {members.length > 0 && (
          <div className="space-y-2">
            {members.map(m => (
              <div
                key={m.user_id}
                className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-border/40 bg-secondary/30"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-xs">
                    {(m.name || m.email || '?').slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{m.name}</p>
                    <p className="text-xs text-muted-foreground">{m.email}</p>
                  </div>
                </div>
                <Badge variant="outline" className="capitalize text-[10px]">
                  {(m.role || 'sales_rep').replace('_', ' ')}
                </Badge>
              </div>
            ))}
          </div>
        )}

        {pendingInvites.length > 0 && (
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Pending invitations</p>
            <div className="space-y-2">
              {pendingInvites.map(inv => (
                <div
                  key={inv.invite_id}
                  className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-amber-500/30 bg-amber-500/5"
                  data-testid={`pending-invite-${inv.invite_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <Mail className="w-4 h-4 text-amber-700" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{inv.email}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Sent {new Date(inv.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="ghost" onClick={() => handleResend(inv.invite_id)} title="Resend">
                      <RotateCw className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => handleRevoke(inv.invite_id)} title="Revoke">
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {pastInvites.length > 0 && (
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer hover:text-foreground">Recent activity</summary>
            <ul className="mt-2 space-y-1 pl-2">
              {pastInvites.map(inv => (
                <li key={inv.invite_id} className="flex items-center gap-2">
                  <span className="capitalize">{inv.status}</span>
                  <span>—</span>
                  <span>{inv.email}</span>
                </li>
              ))}
            </ul>
          </details>
        )}

        {!loading && members.length === 0 && pendingInvites.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">
            No team members yet. Click "Invite Member" to get started.
          </p>
        )}
      </CardContent>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-card border-border sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Invite a team member</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Email address</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="rep@company.com"
                className="bg-background border-border"
                data-testid="invite-email-input"
              />
              <p className="text-xs text-muted-foreground mt-2">
                We'll email an invitation link. It expires in 7 days.
              </p>
            </div>
            <Button
              onClick={handleInvite}
              disabled={submitting}
              className="w-full bg-primary text-primary-foreground"
              data-testid="invite-submit-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Send invitation
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


export default function Team() {
  const navigate = useNavigate();
  const { isManager } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    fetchTeam();
  }, [period]);

  const fetchTeam = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/performance/team?period=${period}`, { credentials: 'include' });
      if (!res.ok) {
        if (res.status === 403) {
          toast.error('Manager access required');
          return;
        }
        throw new Error('Failed to load team data');
      }
      const data = await res.json();
      setMembers(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Rep detail view
  if (selectedMember) {
    return (
      <Layout>
        <RepDetail userId={selectedMember} onBack={() => setSelectedMember(null)} />
      </Layout>
    );
  }

  const filtered = members.filter(m =>
    m.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => b.qualification_accuracy - a.qualification_accuracy);

  const teamAvg = members.length > 0 ? {
    calls: Math.round(members.reduce((s, m) => s + m.calls_analyzed, 0) / members.length),
    discovery: Math.round(members.reduce((s, m) => s + m.avg_discovery_quality, 0) / members.length * 10) / 10,
    questions: Math.round(members.reduce((s, m) => s + m.avg_question_depth, 0) / members.length * 10) / 10,
    objections: Math.round(members.reduce((s, m) => s + m.avg_objection_handling, 0) / members.length * 10) / 10,
    accuracy: Math.round(members.reduce((s, m) => s + m.qualification_accuracy, 0) / members.length * 10) / 10,
  } : null;

  return (
    <Layout>
      <div className="space-y-6" data-testid="team-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground" data-testid="team-heading">
              Team Performance
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {members.length} team member{members.length !== 1 ? 's' : ''} tracked
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="team-search"
                className="pl-9 pr-4 py-2 rounded-lg bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 w-48"
              />
            </div>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32 bg-card border-border" data-testid="team-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="quarter">This Quarter</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Team Members + Invites */}
        <TeamInvitesPanel isManager={isManager} />

        {/* Team Averages */}
        {teamAvg && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="team-averages">
            {[
              { label: 'Avg Calls', value: teamAvg.calls, icon: Phone },
              { label: 'Discovery', value: `${teamAvg.discovery}%`, icon: Search },
              { label: 'Questions', value: `${teamAvg.questions}%`, icon: HelpCircle },
              { label: 'Objections', value: `${teamAvg.objections}%`, icon: Shield },
              { label: 'Win Rate', value: `${teamAvg.accuracy}%`, icon: Target },
            ].map((stat) => (
              <Card key={stat.label} className="bg-card border-border/50">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
                    <stat.icon className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                    <p className="text-lg font-bold text-foreground">{stat.value}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Empty State */}
        {!loading && members.length === 0 && (
          <Card className="bg-card border-border/50">
            <CardContent className="p-12 text-center">
              <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="font-heading text-lg font-semibold text-foreground mb-2">No Team Data Yet</h3>
              <p className="text-sm text-muted-foreground mb-4">Team members will appear here once they start analyzing calls.</p>
              <Button size="sm" onClick={() => navigate('/upload')} className="bg-primary text-primary-foreground">
                Upload First Call
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Member Cards */}
        {!loading && sorted.length > 0 && (
          <div className="grid gap-4" data-testid="team-members-list">
            {sorted.map((member, idx) => (
              <Card
                key={member.user_id}
                data-testid={`team-member-${member.user_id}`}
                className="bg-card border-border/50 hover:border-primary/30 transition-colors cursor-pointer"
                onClick={() => setSelectedMember(member.user_id)}
              >
                <CardContent className="p-5">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-5">
                    {/* Rank + Name */}
                    <div className="flex items-center gap-4 lg:w-56 flex-shrink-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                        idx === 0 ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30' :
                        idx === 1 ? 'bg-slate-400/15 text-slate-300 border border-slate-400/30' :
                        idx === 2 ? 'bg-orange-500/15 text-orange-600 border border-orange-500/30' :
                        'bg-secondary text-muted-foreground border border-border'
                      }`}>
                        {idx === 0 ? <Award className="w-5 h-5" /> : `#${idx + 1}`}
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-foreground">{member.name}</h3>
                        <p className="text-xs text-muted-foreground">{member.calls_analyzed} calls analyzed</p>
                      </div>
                    </div>

                    {/* Deal Stats */}
                    <div className="flex items-center gap-3 lg:w-40 flex-shrink-0">
                      <div className="flex gap-2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-600 text-xs font-medium border border-emerald-500/20">
                          <Zap className="w-3 h-3" />
                          {member.total_hot_deal} Hot
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary/10 text-primary text-xs font-medium border border-primary/20">
                          <Target className="w-3 h-3" />
                          {member.total_sql} SQL
                        </span>
                      </div>
                    </div>

                    {/* Win Rate */}
                    <div className="lg:w-28 flex-shrink-0">
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground mb-0.5">Win Rate</p>
                        <p className={`text-xl font-bold ${
                          member.qualification_accuracy >= 60 ? 'text-emerald-600' :
                          member.qualification_accuracy >= 30 ? 'text-amber-400' : 'text-rose-400'
                        }`}>
                          {member.qualification_accuracy}%
                        </p>
                      </div>
                    </div>

                    {/* Performance Metrics */}
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <MetricBar label="Discovery" value={member.avg_discovery_quality} icon={Search} color="primary" />
                      <MetricBar label="Questions" value={member.avg_question_depth} icon={HelpCircle} color="violet" />
                      <MetricBar label="Objections" value={member.avg_objection_handling} icon={Shield} color="amber" />
                      <MetricBar label="Talk Ratio" value={member.avg_talk_ratio} icon={MessageSquare} color="emerald" />
                      <MetricBar label="Next Steps" value={member.avg_next_step_clarity} icon={TrendingUp} color="rose" />
                    </div>

                    {/* Arrow indicator */}
                    <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0 hidden lg:block" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
